import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Sentry } from '../config/sentry';

type State = {
  hasError: boolean;
};

type ErrorBoundaryClassProps = {
  children: ReactNode;
  fallback?: ReactNode;
  onError: (error: Error, errorInfo?: ErrorInfo) => void;
};

class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, State> {
  constructor(props: ErrorBoundaryClassProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, { extra: { errorInfo } });
    this.props.onError(error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-4 m-4 bg-red-50 border border-red-200 rounded-md">
          <h2 className="text-lg font-semibold text-red-800">Something went wrong</h2>
          <p className="text-red-600">The application encountered an error. Please refresh the page and try again.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const ErrorBoundaryWithContext: React.FC<{ children: ReactNode; fallback?: ReactNode }> = ({ children, fallback }) => (
  <ErrorBoundaryClass
    onError={(error) => console.error('Error caught by ErrorBoundary:', error)}
    fallback={fallback}
  >
    {children}
  </ErrorBoundaryClass>
);

export default ErrorBoundaryWithContext;
